/**
 * Admin Active Sessions Page
 * ==========================
 * View and manage all active sessions across the platform.
 * Super Admin can:
 * - View all active sessions with device info
 * - Filter by role, search by email/name/IP
 * - Revoke individual sessions
 * - Revoke all sessions for a user
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Clock,
  Shield,
  Users,
  RefreshCw,
  Search,
  XCircle,
  AlertTriangle,
  Activity,
  MapPin,
  Chrome,
  // Firefox,
  // Safari
} from 'lucide-react';

const AdminActiveSessions = () => {
  const { apiCall, isSuperAdmin } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Dialog states
  const [revokeDialog, setRevokeDialog] = useState({ open: false, session: null });
  const [revokeAllDialog, setRevokeAllDialog] = useState({ open: false, userId: null, userEmail: null });
  const [userSessionsDialog, setUserSessionsDialog] = useState({ open: false, userId: null, sessions: [] });

  useEffect(() => {
    fetchSessions();
    fetchStats();
  }, [pagination.page, roleFilter]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      if (roleFilter && roleFilter !== 'all') {
        params.append('user_role', roleFilter);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const data = await apiCall(`/api/security/admin/sessions?${params}`);
      if (data.success) {
        setSessions(data.data || []);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load active sessions',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiCall('/api/security/admin/sessions/stats');
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSessions();
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      const data = await apiCall(`/api/security/admin/sessions/${sessionId}`, {
        method: 'DELETE'
      });
      if (data.success) {
        toast({
          title: 'Session Revoked',
          description: data.message
        });
        fetchSessions();
        fetchStats();
        setRevokeDialog({ open: false, session: null });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to revoke session',
        variant: 'destructive'
      });
    }
  };

  const handleRevokeAllUserSessions = async (userId) => {
    try {
      const data = await apiCall(`/api/security/admin/sessions/revoke-user/${userId}`, {
        method: 'POST'
      });
      if (data.success) {
        toast({
          title: 'Sessions Revoked',
          description: data.message
        });
        fetchSessions();
        fetchStats();
        setRevokeAllDialog({ open: false, userId: null, userEmail: null });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to revoke sessions',
        variant: 'destructive'
      });
    }
  };

  const viewUserSessions = async (userId) => {
    try {
      const data = await apiCall(`/api/security/admin/sessions/user/${userId}`);
      if (data.success) {
        setUserSessionsDialog({
          open: true,
          userId,
          user: data.user,
          sessions: data.active_sessions
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load user sessions',
        variant: 'destructive'
      });
    }
  };

  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="w-4 h-4" />;
      case 'tablet':
        return <Tablet className="w-4 h-4" />;
      default:
        return <Monitor className="w-4 h-4" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'admin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'vendor':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span>Only Super Admins can access session management.</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Activity className="w-7 h-7 text-blue-600" />
              Active Sessions
            </h1>
            <p className="text-slate-600 mt-1">
              Monitor and manage active sessions across the platform
            </p>
          </div>
          <Button
            onClick={() => { fetchSessions(); fetchStats(); }}
            variant="outline"
            disabled={loading}
            data-testid="refresh-sessions-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total_active_sessions}</p>
                    <p className="text-xs text-slate-500">Active Sessions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.unique_users_logged_in}</p>
                    <p className="text-xs text-slate-500">Logged In Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.sessions_last_24h}</p>
                    <p className="text-xs text-slate-500">Last 24 Hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.by_device || {}).map(([device, count]) => (
                    <Badge key={device} variant="outline" className="text-xs">
                      {device}: {count}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">By Device Type</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by email, name, or IP..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="vendor">Vendors</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                  <SelectItem value="super_admin">Super Admins</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" data-testid="search-sessions-btn">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Sessions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Active Sessions ({pagination.total})</CardTitle>
            <CardDescription>
              Real-time view of all active user sessions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-16 bg-slate-100 rounded"></div>
                ))}
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No active sessions found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Location / IP</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{session.user_name || 'Unknown'}</span>
                          <span className="text-sm text-slate-500">{session.user_email}</span>
                          <Badge className={`w-fit mt-1 text-xs ${getRoleBadgeColor(session.user_role)}`}>
                            {session.user_role?.replace('_', ' ')}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getDeviceIcon(session.device)}
                          <div className="flex flex-col">
                            <span className="text-sm">{session.device || 'Unknown'}</span>
                            <span className="text-xs text-slate-500">
                              {session.browser} / {session.os}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">
                              {session.location_data?.city && session.location_data?.country 
                                ? `${session.location_data.city}, ${session.location_data.country}`
                                : session.location || 'Unknown'}
                            </span>
                            <span className="text-xs text-slate-500">{session.ip_address}</span>
                            {session.location_data?.isp && (
                              <span className="text-xs text-slate-400">{session.location_data.isp}</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className={`w-4 h-4 ${session.is_idle ? 'text-amber-500' : 'text-slate-400'}`} />
                          <div className="flex flex-col">
                            <span className="text-sm">{formatTimeAgo(session.last_activity)}</span>
                            {session.is_idle && (
                              <span className="text-xs text-amber-600">
                                Idle ({session.idle_minutes}m)
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewUserSessions(session.user_id)}
                            data-testid={`view-user-sessions-${session.id}`}
                          >
                            View All
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setRevokeDialog({ open: true, session })}
                            data-testid={`revoke-session-${session.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-slate-500">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revoke Single Session Dialog */}
        <AlertDialog open={revokeDialog.open} onOpenChange={(open) => setRevokeDialog({ open, session: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Revoke Session?</AlertDialogTitle>
              <AlertDialogDescription>
                This will immediately log out <strong>{revokeDialog.session?.user_email}</strong> from 
                their <strong>{revokeDialog.session?.device}</strong> device. They will need to log in again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={() => handleRevokeSession(revokeDialog.session?.id)}
              >
                Revoke Session
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* User Sessions Dialog */}
        <Dialog 
          open={userSessionsDialog.open} 
          onOpenChange={(open) => setUserSessionsDialog({ open, userId: null, sessions: [] })}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Sessions for {userSessionsDialog.user?.name || userSessionsDialog.user?.email}
              </DialogTitle>
              <DialogDescription>
                {userSessionsDialog.sessions?.length} active session(s)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {userSessionsDialog.sessions?.map((session) => (
                <div 
                  key={session.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getDeviceIcon(session.device)}
                    <div>
                      <p className="font-medium text-sm">
                        {session.device} - {session.browser}
                      </p>
                      <p className="text-xs text-slate-500">
                        {session.location_data?.display || session.location || 'Unknown'} • {session.ip_address}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatTimeAgo(session.last_activity)}
                        {session.location_data?.isp && ` • ${session.location_data.isp}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleRevokeSession(session.id)}
                  >
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setUserSessionsDialog({ open: false, userId: null, sessions: [] })}
              >
                Close
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setRevokeAllDialog({
                    open: true,
                    userId: userSessionsDialog.user?.id,
                    userEmail: userSessionsDialog.user?.email
                  });
                  setUserSessionsDialog({ open: false, userId: null, sessions: [] });
                }}
              >
                Revoke All Sessions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Revoke All User Sessions Dialog */}
        <AlertDialog open={revokeAllDialog.open} onOpenChange={(open) => setRevokeAllDialog({ open, userId: null, userEmail: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Revoke All Sessions?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will immediately log out <strong>{revokeAllDialog.userEmail}</strong> from 
                <strong> ALL devices</strong>. They will need to log in again on each device.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={() => handleRevokeAllUserSessions(revokeAllDialog.userId)}
              >
                Revoke All Sessions
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminActiveSessions;
